/**
 * **屏幕 ↔ URL。**
 *
 * 目标：每个 screen 有一个可以直接打开 / 刷新 / 前进后退的地址。
 *
 * - **路径式**（`/play`），不是 hash 式 —— 干净、能当 PWA 的 start_url；
 * - ⚠️ **路径不带尾斜杠**：产物用相对路径（`base: './'`），`/room/` 会把
 *   `./assets/...` 解析成 `/room/assets/...`；
 * - 现有的 `#ABCD` 邀请链接**照旧能用**：hash 里有合法房间号时优先走它
 *   （见 `room.ts` 的 `roomCodeFromHash`）。
 *
 * 这一层是纯的（只 import 常量与 hash 解析），能在 node 里断言。
 */
import { SCREEN } from './screens/ids';
import { roomCodeFromHash } from './room';
import type { ScreenId, StackState } from './stack';

export interface Route {
  readonly screen: ScreenId;
  /** 从 hash 读到的合法房间号（老邀请链接）。没有就是 null */
  readonly roomCode: string | null;
}

const PATH_TO_SCREEN: Record<string, ScreenId> = {
  '/': SCREEN.title,
  '/play': SCREEN.meetup,
  '/room': SCREEN.room,
  '/hotseat': SCREEN.hotseat,
  '/game': SCREEN.game,
  '/credits': SCREEN.credit,
  '/changelog': SCREEN.changelog,
};

const SCREEN_TO_PATH: Record<string, string> = {
  [SCREEN.title]: '/',
  [SCREEN.meetup]: '/play',
  [SCREEN.room]: '/room',
  [SCREEN.hotseat]: '/hotseat',
  [SCREEN.game]: '/game',
  [SCREEN.credit]: '/credits',
  [SCREEN.changelog]: '/changelog',
};

/** 丢掉 query，并去掉尾斜杠（根路径保留 `/`） */
export function normalizePath(pathname: string): string {
  const noQuery = pathname.split('?')[0] ?? '/';
  if (noQuery.length <= 1) return '/';
  return noQuery.endsWith('/') ? noQuery.slice(0, -1) : noQuery;
}

/** 解析当前地址。hash 房间号优先于路径 */
export function parseRoute(pathname: string, hash: string): Route {
  const code = roomCodeFromHash(hash);
  if (code !== null) return { screen: SCREEN.meetup, roomCode: code };
  return { screen: PATH_TO_SCREEN[normalizePath(pathname)] ?? SCREEN.title, roomCode: null };
}

/**
 * 栈 → 路径。`connecting` 是过渡屏，不改地址（返回 null）——
 * 这样「#ABCD 自动进房」那条路上地址不会被中途改掉。
 */
export function routePathFor(state: StackState): string | null {
  const screen = state.screens[state.screens.length - 1];
  if (screen === undefined) return '/';
  // 过渡屏不占地址：`#ABCD` 自动进房那条路上地址不该被中途改掉
  if (screen === SCREEN.connecting) return null;
  return SCREEN_TO_PATH[screen] ?? '/';
}
